import { useState, useEffect, useRef } from 'react'
import { Search, Plus, Edit, Trash2, X, Ban, CheckCircle, ChevronLeft, ChevronRight, Eye, EyeOff, Lock } from 'lucide-react'
import toast from 'react-hot-toast'
import { adminApi, RBACRole, AdminUserRoleItem } from '../../lib/api/admin'
import { usePermissionStore } from '../../stores/permissionStore'
import { friendlyApiError } from '../../lib/utils/apiError'

interface UserItem {
  id: number
  full_name: string | null
  alias: string | null
  email: string
  role: string
  is_active?: boolean
  created_at: string
  is_available?: boolean | null
  admin_locked?: boolean | null
}

const PER_PAGE = 10

// Returns true when an unknown error is an HTTP 403
function is403(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 403
}

// A select value of "rbac:5" means: create as admin + assign RBAC role 5.
// Values "client" and "interviewer" are base roles with no RBAC assignment.
// Value "admin" is a read-only sentinel for existing super admins (not offered as a new option).
function parseRoleValue(val: string): { baseRole: string; rbacRoleId: number | null } {
  if (val.startsWith('rbac:')) return { baseRole: 'admin', rbacRoleId: Number(val.slice(5)) }
  return { baseRole: val, rbacRoleId: null }
}

export default function AdminUsers() {
  const { can } = usePermissionStore()
  const canCreate = can('users', 'create')
  const canEdit   = can('users', 'edit')
  const canDelete = can('users', 'delete')
  const [users, setUsers] = useState<UserItem[]>([])
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [lastPage, setLastPage] = useState(1)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState<UserItem | null>(null)
  const [loading, setLoading] = useState(true)
  const [newUser, setNewUser] = useState({ full_name: '', email: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  // Select value for Add modal: "client" | "interviewer" | "rbac:{id}"
  const [newRoleValue, setNewRoleValue] = useState('client')
  const [editData, setEditData] = useState({
    full_name: '',
    email: '',
    // baseRole tracks the actual DB role for alias-field logic
    baseRole: 'client',
    is_active: true,
    alias: '',
  })
  // Select value for Edit modal: "client" | "interviewer" | "rbac:{id}" | "admin" (super admin sentinel)
  const [editRoleValue, setEditRoleValue] = useState('client')
  const [emailError, setEmailError] = useState('')
  const [saving, setSaving] = useState(false)
  const [showBanModal, setShowBanModal] = useState<UserItem | null>(null)
  const [banning, setBanning] = useState(false)
  const [rbacRoles, setRbacRoles] = useState<RBACRole[]>([])
  const [adminUserRoles, setAdminUserRoles] = useState<AdminUserRoleItem[]>([])

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounce search: reset to page 1 and fetch 300 ms after typing stops
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setPage(1)
      fetchUsers()
    }, 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [search]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch immediately when page or role filter changes (no debounce needed)
  useEffect(() => {
    fetchUsers()
  }, [page, roleFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load RBAC roles and current admin role assignments once
  useEffect(() => {
    adminApi.getRoles().then((r) => setRbacRoles(r.filter((role) => !role.is_system))).catch(() => {})
    adminApi.getAdminUsersWithRoles().then(setAdminUserRoles).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const params: Record<string, unknown> = { page, per_page: PER_PAGE }
      if (roleFilter !== 'all') params.role = roleFilter
      if (search) params.search = search
      const res = await adminApi.getUsers(params)
      if (res && typeof res === 'object' && 'total' in res) {
        setUsers(Array.isArray(res.data) ? res.data : [])
        setTotal(res.total ?? 0)
        setLastPage(res.last_page ?? 1)
      } else {
        setUsers(Array.isArray(res) ? res : [])
        setTotal(Array.isArray(res) ? res.length : 0)
        setLastPage(1)
      }
    } catch (err) {
      toast.error(is403(err) ? "You don't have permission to view users." : friendlyApiError(err, 'Failed to load users.'))
    } finally {
      setLoading(false)
    }
  }

  const handleRoleFilter = (role: string) => {
    setRoleFilter(role)
    setPage(1)
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    const { baseRole, rbacRoleId } = parseRoleValue(newRoleValue)
    try {
      const payload: Record<string, unknown> = { ...newUser, role: baseRole }
      if (rbacRoleId !== null) payload.rbac_role_id = rbacRoleId
      const created = await adminApi.createUser(payload)
      // Update local RBAC role state so the role badge renders immediately
      if (rbacRoleId !== null) {
        const assignedRole = rbacRoles.find((r) => r.id === rbacRoleId)
        if (assignedRole) {
          setAdminUserRoles((prev) => [
            ...prev.filter((r) => r.user_id !== created.id),
            { user_id: created.id, user_email: created.email, role_id: rbacRoleId, role_name: assignedRole.name, is_super_admin: false },
          ])
        }
      }
      setShowAddModal(false)
      setNewUser({ full_name: '', email: '', password: '' })
      setShowPassword(false)
      setNewRoleValue('client')
      setPage(1)
      fetchUsers()
      toast.success('User created successfully.')
    } catch (err: unknown) {
      toast.error(is403(err) ? "You don't have permission to create a user." : friendlyApiError(err, 'Failed to create user.'))
    } finally {
      setSaving(false)
    }
  }

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!showEditModal) return
    if (!isValidEmail(editData.email)) {
      setEmailError('Please enter a valid email address (e.g. user@example.com).')
      return
    }
    setSaving(true)
    const { baseRole, rbacRoleId } = parseRoleValue(editRoleValue)
    try {
      const payload: Record<string, unknown> = {
        full_name: editData.full_name.trim() || null,
        email: editData.email,
        role: baseRole,
        is_active: editData.is_active,
      }
      // Only send alias when changing TO interviewer role (needed to create the profile)
      if (baseRole === 'interviewer' && showEditModal.role !== 'interviewer') {
        payload.alias = editData.alias.trim()
      }
      await adminApi.updateUser(showEditModal.id, payload)
      if (baseRole === 'admin') {
        try {
          const assignment = await adminApi.assignUserRole(showEditModal.id, rbacRoleId)
          setAdminUserRoles((prev) => [...prev.filter((r) => r.user_id !== showEditModal.id), assignment])
        } catch {
          // Role assignment failure is non-fatal
        }
      }
      setShowEditModal(null)
      // Re-fetch with the active role filter so users who no longer match the
      // current tab (e.g. admin→client while "Admins" filter is active) are
      // immediately removed from the list instead of staying stale.
      fetchUsers()
      toast.success('User updated successfully.')
    } catch (err) {
      toast.error(is403(err) ? "You don't have permission to edit this user." : friendlyApiError(err, 'Failed to update user.'))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteUser = async (id: number) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return
    try {
      await adminApi.deleteUser(id)
      fetchUsers()
      toast.success('User deactivated successfully.')
    } catch (err) {
      toast.error(is403(err) ? "You don't have permission to deactivate this user." : friendlyApiError(err, 'Failed to deactivate user.'))
    }
  }

  const handleBanToggle = async () => {
    if (!showBanModal) return
    const isCurrentlyLocked = showBanModal.admin_locked === true
    setBanning(true)
    try {
      const result = await adminApi.setInterviewerAvailability(showBanModal.id, isCurrentlyLocked)
      setUsers(users.map((u) =>
        u.id === showBanModal.id
          ? { ...u, is_available: result.is_available, admin_locked: result.admin_locked }
          : u
      ))
      setShowBanModal(null)
      toast.success(isCurrentlyLocked ? 'Interviewer marked as available.' : 'Interviewer marked as unavailable.')
    } catch (err) {
      toast.error(is403(err) ? "You don't have permission to change interviewer availability." : friendlyApiError(err, 'Failed to update availability.'))
    } finally {
      setBanning(false)
    }
  }

  const isValidEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())

  const suggestAlias = (user: UserItem): string => {
    const base = user.full_name || user.email.split('@')[0]
    return base.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  }

  const openEditModal = (user: UserItem) => {
    setShowEditModal(user)
    setEmailError('')
    setEditData({
      // For interviewers, their displayed name is the alias; for clients it's full_name
      full_name: user.role === 'interviewer' ? (user.alias || '') : (user.full_name || ''),
      email: user.email,
      baseRole: user.role,
      is_active: user.is_active !== false,
      alias: user.alias || '',
    })
    if (user.role === 'admin') {
      const assignment = adminUserRoles.find((r) => r.user_id === user.id)
      // If they have an RBAC role → select it; otherwise they're a super admin (sentinel "admin")
      setEditRoleValue(assignment?.role_id != null ? `rbac:${assignment.role_id}` : 'admin')
    } else {
      setEditRoleValue(user.role)
    }
  }

  const closeEditModal = () => {
    setShowEditModal(null)
    setEmailError('')
    setEditRoleValue('client')
  }

  // Returns true if the user is a super admin (cannot be edited or deleted by anyone)
  const isSuperAdmin = (user: UserItem): boolean => {
    if (user.role !== 'admin') return false
    const assignment = adminUserRoles.find((r) => r.user_id === user.id)
    return assignment?.is_super_admin === true
  }

  // Returns the display label for a user's role in the table
  const roleDisplayName = (user: UserItem): string => {
    if (user.role !== 'admin') return user.role.charAt(0).toUpperCase() + user.role.slice(1)
    const assignment = adminUserRoles.find((r) => r.user_id === user.id)
    return assignment?.role_name ?? 'Admin'
  }

  const roleBadgeClass = (user: UserItem): string => {
    if (user.role === 'admin') return 'badge-danger'
    if (user.role === 'interviewer') return 'badge-primary'
    return 'badge-success'
  }

  return (
    <div>
      <div
        className="page-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}
      >
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="page-subtitle">Manage clients and interviewers</p>
        </div>
        {canCreate && (
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <Plus size={18} />
            Add User
          </button>
        )}
      </div>

      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          {/* Search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200 }}>
            <Search size={18} style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              className="form-input"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ border: 'none', boxShadow: 'none' }}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Role filter */}
          <div style={{ display: 'flex', gap: 8 }}>
            {['all', 'client', 'interviewer', 'admin'].map((role) => (
              <button
                key={role}
                onClick={() => handleRoleFilter(role)}
                style={{
                  padding: '6px 14px',
                  border: '1px solid var(--border)',
                  background: roleFilter === role ? 'var(--primary-navy)' : 'white',
                  color: roleFilter === role ? 'white' : 'var(--text-muted)',
                  borderRadius: 20,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {role === 'all' ? 'All' : role.charAt(0).toUpperCase() + role.slice(1) + 's'}
              </button>
            ))}
          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Join Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                    Loading...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div className="avatar">
                          {(user.full_name || user.alias || user.email).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{user.full_name || user.alias || '—'}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${roleBadgeClass(user)}`}>
                        {roleDisplayName(user)}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span className={`badge ${user.is_active !== false ? 'badge-success' : 'badge-danger'}`}>
                          {user.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                        {user.role === 'interviewer' && user.admin_locked && (
                          <span className="badge badge-danger" style={{ fontSize: 10 }}>
                            Unavailable (Admin)
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        {isSuperAdmin(user) ? (
                          /* Super admins are protected — no one can edit or delete them */
                          <span
                            title="Super admin accounts cannot be edited or deleted"
                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: 'rgba(30,36,50,0.06)', borderRadius: 6, color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, cursor: 'default', userSelect: 'none' }}
                          >
                            <Lock size={13} /> Protected
                          </span>
                        ) : (
                          <>
                            {/* 1. Edit */}
                            {canEdit && (
                              <button
                                onClick={() => openEditModal(user)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: 'var(--text-muted)' }}
                                title="Edit"
                              >
                                <Edit size={16} />
                              </button>
                            )}
                            {/* 2. Delete / Deactivate */}
                            {canDelete && (
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: 'var(--danger)' }}
                                title="Deactivate"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                            {/* 3. Unavailable (interviewers only) */}
                            {user.role === 'interviewer' && canEdit && (
                              <button
                                onClick={() => setShowBanModal(user)}
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer', padding: 6,
                                  color: user.admin_locked ? '#16a34a' : '#d97706',
                                }}
                                title={user.admin_locked ? 'Mark as Available' : 'Mark as Unavailable'}
                              >
                                {user.admin_locked ? <CheckCircle size={16} /> : <Ban size={16} />}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 16,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
              fontSize: 14,
            }}
          >
            <span style={{ color: 'var(--text-muted)' }}>
              Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} of {total} users
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 6,
                  background: 'white', cursor: page === 1 ? 'not-allowed' : 'pointer',
                  opacity: page === 1 ? 0.4 : 1, fontSize: 13,
                }}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              {Array.from({ length: lastPage }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === lastPage || Math.abs(p - page) <= 1)
                .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...')
                  acc.push(p)
                  return acc
                }, [])
                .map((p, idx) =>
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: 'var(--text-muted)' }}>…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p as number)}
                      style={{
                        width: 32, height: 32, border: '1px solid var(--border)', borderRadius: 6,
                        background: page === p ? 'var(--primary-navy)' : 'white',
                        color: page === p ? 'white' : 'var(--text-primary)',
                        cursor: 'pointer', fontSize: 13, fontWeight: page === p ? 600 : 400,
                      }}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page === lastPage}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 6,
                  background: 'white', cursor: page === lastPage ? 'not-allowed' : 'pointer',
                  opacity: page === lastPage ? 0.4 : 1, fontSize: 13,
                }}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); setShowPassword(false) }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add New User</h3>
              <button onClick={() => { setShowAddModal(false); setShowPassword(false) }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text" className="form-input" placeholder="Enter full name"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email" className="form-input" placeholder="Enter email address"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'} className="form-input" placeholder="Enter password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      required minLength={6}
                      style={{ paddingRight: 40 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-input"
                    value={newRoleValue}
                    onChange={(e) => setNewRoleValue(e.target.value)}
                  >
                    <option value="client">Client</option>
                    <option value="interviewer">Interviewer</option>
                    {rbacRoles.length > 0 && (
                      <optgroup label="Admin Roles">
                        {rbacRoles.map((r) => (
                          <option key={r.id} value={`rbac:${r.id}`}>{r.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {parseRoleValue(newRoleValue).baseRole === 'admin' && (
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                      This user will have admin access restricted to the permissions defined for this role in Security &gt; Access Controls.
                    </p>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowAddModal(false); setShowPassword(false) }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ban / Availability Modal */}
      {showBanModal && (
        <div className="modal-overlay" onClick={() => !banning && setShowBanModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h3 className="modal-title">
                {showBanModal.admin_locked ? 'Mark Interviewer as Available' : 'Mark Interviewer as Unavailable'}
              </h3>
              <button onClick={() => !banning && setShowBanModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {showBanModal.admin_locked ? (
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  Do you want to mark <strong>{showBanModal.alias || showBanModal.full_name || showBanModal.email}</strong> as <strong>Available</strong>?
                  They will be able to accept new interview bookings again.
                </p>
              ) : (
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  Do you want to mark <strong>{showBanModal.alias || showBanModal.full_name || showBanModal.email}</strong> as <strong>Unavailable</strong>?
                  If marked unavailable, the interviewer will not be able to take new interviews.
                  Only an admin can make them available again.
                </p>
              )}
              <div
                style={{
                  marginTop: 14, padding: '10px 14px', borderRadius: 8,
                  background: showBanModal.admin_locked ? 'rgba(22,163,74,0.06)' : 'rgba(220,38,38,0.06)',
                  border: `1px solid ${showBanModal.admin_locked ? 'rgba(22,163,74,0.2)' : 'rgba(220,38,38,0.2)'}`,
                  fontSize: 13, color: showBanModal.admin_locked ? '#15803d' : '#dc2626',
                }}
              >
                {showBanModal.admin_locked
                  ? '✓ This interviewer is currently unavailable by admin decision.'
                  : '⚠ This action can only be reversed by an admin.'}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowBanModal(null)} disabled={banning}>Cancel</button>
              <button
                type="button" className="btn"
                style={{ background: showBanModal.admin_locked ? '#16a34a' : '#dc2626', color: '#fff', border: 'none' }}
                onClick={handleBanToggle} disabled={banning}
              >
                {banning ? 'Saving…' : showBanModal.admin_locked ? 'Yes, Make Available' : 'Yes, Mark Unavailable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit User</h3>
              <button onClick={closeEditModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleEditUser}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Full name"
                    value={editData.full_name}
                    onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                    maxLength={255}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editData.email}
                    onChange={(e) => {
                      const val = e.target.value
                      setEditData({ ...editData, email: val })
                      setEmailError(val && !isValidEmail(val) ? 'Invalid email address.' : '')
                    }}
                    required
                    style={emailError ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 2px rgba(220,38,38,0.15)' } : {}}
                  />
                  {emailError && (
                    <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{emailError}</p>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-input"
                    value={editRoleValue}
                    onChange={(e) => {
                      const val = e.target.value
                      const { baseRole } = parseRoleValue(val)
                      const autoAlias =
                        baseRole === 'interviewer' && showEditModal?.role !== 'interviewer'
                          ? suggestAlias(showEditModal!)
                          : editData.alias
                      setEditRoleValue(val)
                      setEditData({ ...editData, baseRole, alias: autoAlias })
                    }}
                  >
                    <option value="client">Client</option>
                    <option value="interviewer">Interviewer</option>
                    {rbacRoles.length > 0 && (
                      <optgroup label="Admin Roles">
                        {rbacRoles.map((r) => (
                          <option key={r.id} value={`rbac:${r.id}`}>{r.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {/* Sentinel option for existing admins with no role assigned — not selectable as a new choice */}
                    {editRoleValue === 'admin' && (
                      <option value="admin" disabled>Admin (no role assigned)</option>
                    )}
                  </select>
                  {editRoleValue === 'admin' && (
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                      This user has no role assigned. Select an Admin Role above to restrict their access.
                    </p>
                  )}
                </div>
                {editData.baseRole === 'interviewer' && showEditModal?.role !== 'interviewer' && (
                  <div className="form-group">
                    <label className="form-label">
                      Interviewer Alias <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. john-smith"
                      value={editData.alias}
                      onChange={(e) => setEditData({ ...editData, alias: e.target.value })}
                      required
                      minLength={2}
                      maxLength={100}
                    />
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                      Public display name shown to clients. Lowercase letters, numbers and hyphens only.
                    </p>
                  </div>
                )}
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox" checked={editData.is_active}
                      onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })}
                    />
                    Active Account
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeEditModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
